import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Phone, MessageSquare, Video, MessageCircle, ShieldCheck, AlertTriangle, ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-organization CALLACHAP page. Reads settings from the public
 * /api/callachap/{slug} endpoint. Active-org scoped, no cross-org leakage.
 * Employees who arrive without a slug are sent to the bare fallback page.
 */
export default function CallAChapOrgPage() {
  const { orgSlug } = useParams();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!orgSlug) return;
    setLoading(true);
    axios.get(`${API}/callachap/${encodeURIComponent(orgSlug)}`)
      .then((r) => { setCfg(r.data); try { sessionStorage.setItem("callachap.slug", orgSlug); } catch {} })
      .catch((err) => { if (err.response?.status === 404) setNotFound(true); else toast.error(err.message); })
      .finally(() => setLoading(false));
  }, [orgSlug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-sm text-slate-500" data-testid="callachap-loading">Loading…</div>;
  if (notFound) return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="callachap-not-found">
      <AlertTriangle className="w-8 h-8 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
      <h1 className="font-heading text-2xl text-slate-900">CALLACHAP isn't set up for that link</h1>
      <p className="mt-2 text-sm text-slate-600">Please check with your organization's HR or contact your CCCHAPS chaplain team for the correct link.</p>
      <button onClick={() => nav("/care-portal/callachap")} className="mt-4 inline-flex items-center gap-1 text-xs text-[#4A6FA5] hover:text-[#3B597B]" data-testid="callachap-goto-fallback"><ArrowLeft className="w-3.5 h-3.5" /> Enter an access code instead</button>
    </div>
  );
  if (!cfg) return null;

  const orgName = cfg.organization?.name || "";
  const orgLogo = cfg.organization?.logo_url || "";
  const anyEnabled = cfg.call?.enabled || cfg.text?.enabled || cfg.video?.enabled;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="callachap-org-page">
      <Toaster richColors position="top-center" />

      {/* Company identity */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold" data-testid="callachap-provider-label">
            CALLACHAP · provided by CCCHAPS
          </p>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl text-slate-900 tracking-tight" data-testid="callachap-welcome-heading">
            Welcome to CALLACHAP
          </h1>
          {orgName && (
            <p className="mt-2 text-sm sm:text-base text-slate-700 break-words" data-testid="callachap-org-line">
              Confidential chaplain support provided for <span className="font-semibold text-slate-900">{orgName}</span>.
            </p>
          )}
          {cfg.default_chaplain_display_name && (
            <p className="mt-1 text-xs text-slate-500" data-testid="callachap-chaplain-line">
              Your chaplain: <span className="font-semibold text-slate-700">{cfg.default_chaplain_display_name}</span>
            </p>
          )}
        </div>
        {orgLogo && (
          <div
            className="shrink-0 rounded-lg border border-slate-200 bg-white p-2 h-16 sm:h-20 w-32 sm:w-40 flex items-center justify-center"
            data-testid="callachap-org-logo"
          >
            <img
              src={orgLogo}
              alt={`${orgName} logo`}
              className="max-h-full max-w-full w-auto h-auto object-contain pointer-events-none select-none"
              draggable="false"
            />
          </div>
        )}
      </div>

      {/* Intent */}
      <p className="mt-6 text-sm text-slate-600 leading-relaxed max-w-2xl" data-testid="callachap-intent-line">
        Choose the way you would like to connect with the next available chaplain from your organization's CALLACHAP care team.
      </p>

      {/* Privacy reassurance */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3" data-testid="callachap-privacy-block">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 text-[#4A6FA5] shrink-0" strokeWidth={2.2} />
          <p className="text-sm text-slate-800 font-medium">
            Your conversation is private. Your employer does not receive the private details of your conversation.
          </p>
        </div>
        <p className="mt-2 text-xs text-slate-500 leading-relaxed pl-6">
          Chaplain conversations are confidential within applicable ethical and legal limits, including legally required disclosures and serious safety concerns. Organizations receive aggregate, non-identifying utilization reports; routine reports do not include employee names or written accounts of confidential conversations.
        </p>
      </div>

      {/* Channel tiles */}
      {!anyEnabled ? (
        <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-8 text-center" data-testid="callachap-all-disabled">
          <p className="text-sm text-slate-800 font-medium">CALLACHAP is being set up for your organization.</p>
          <p className="text-xs text-slate-500 mt-1">Please use <Link to="/care-portal/request-support" className="text-[#4A6FA5] underline">Send a Confidential Message</Link> in the meantime.</p>
        </div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="callachap-tiles-grid">
          <CallTile cfg={cfg} orgSlug={orgSlug} />
          <TextTile cfg={cfg} orgSlug={orgSlug} />
          <MessageTile />
          <RemoteSupportTile cfg={cfg} orgSlug={orgSlug} />
        </div>
      )}

      {/* 24/7 note */}
      <p className="mt-6 text-xs text-slate-500 leading-relaxed" data-testid="callachap-247-note">
        24/7 on-call chaplain access is available through your organization's care arrangement. Response times may vary by channel.
        {cfg.hours_note ? <> Organization-specific hours: {cfg.hours_note}.</> : null}
      </p>

      {/* Emergency disclaimer */}
      <div className="mt-4 flex items-start gap-2 text-xs text-slate-500" data-testid="callachap-emergency-note">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.2} />
        <p>
          CALLACHAP is not an emergency, medical, or psychiatric service. If you are experiencing a life-threatening emergency, call 911.
        </p>
      </div>
    </div>
  );
}

// ---- shared bits ----
function OptInIntent({ orgSlug, channel, onDone }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!consent) { toast.error("Please check the opt-in box or tap the number directly."); return; }
    if (!email) { toast.error("Email is required for the intent record."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/callachap/${encodeURIComponent(orgSlug)}/intent`, { channel, first_name: firstName, email, consent: true });
      toast.success("CCCHAPS notified — expect a follow-up if needed.");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSubmitting(false); }
  };
  return (
    <div className="mt-3 rounded border border-slate-200 bg-slate-50/50 p-3" data-testid={`callachap-intent-${channel}`}>
      <label className="flex items-start gap-2 text-xs text-slate-700">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid={`callachap-intent-consent-${channel}`} className="mt-0.5 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
        <span>Let CCCHAPS know I'm reaching out (recommended, optional).</span>
      </label>
      {consent && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input placeholder="First name (optional)" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid={`callachap-intent-firstname-${channel}`} className="px-2 py-1 rounded border border-slate-300 text-xs" />
          <input placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`callachap-intent-email-${channel}`} className="px-2 py-1 rounded border border-slate-300 text-xs" />
          <button onClick={submit} disabled={submitting} data-testid={`callachap-intent-submit-${channel}`} className="col-span-2 px-3 py-1.5 rounded bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-60">{submitting ? "Sending…" : "Notify CCCHAPS"}</button>
        </div>
      )}
    </div>
  );
}

function CallTile({ cfg, orgSlug }) {
  const c = cfg.call || { enabled: false };
  if (!c.enabled) return <DisabledTile icon={Phone} title="Start a Call" body="Not yet configured for your organization." />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="callachap-tile-call">
      <div className="flex items-center gap-2 text-slate-900"><Phone className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} /><h3 className="font-heading text-lg">Call a Chaplain</h3></div>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">
        Connect by phone with an available chaplain from your organization's CALLACHAP care team.
        {c.label ? <> <span className="text-slate-400">{c.label}</span></> : null}
      </p>
      <a href={`tel:${c.value}`} data-testid="callachap-call-link" className="mt-4 block text-center px-4 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider">
        Start a Call{c.value ? ` · ${c.value}` : ""}
      </a>
      <OptInIntent orgSlug={orgSlug} channel="call" />
    </div>
  );
}

function TextTile({ cfg, orgSlug }) {
  const t = cfg.text || { enabled: false };
  if (!t.enabled) return <DisabledTile icon={MessageSquare} title="Send a Text" body="Not yet configured for your organization." />;
  const sms = t.prefill ? `sms:${t.value}?body=${encodeURIComponent(t.prefill)}` : `sms:${t.value}`;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="callachap-tile-text">
      <div className="flex items-center gap-2 text-slate-900"><MessageSquare className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} /><h3 className="font-heading text-lg">Text a Chaplain</h3></div>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">Send a text when writing feels easier than making a call. Text is not a live-chat channel.</p>
      <a href={sms} data-testid="callachap-text-link" className="mt-4 block text-center px-4 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider">
        Send a Text{t.value ? ` · ${t.value}` : ""}
      </a>
      <OptInIntent orgSlug={orgSlug} channel="text" />
    </div>
  );
}

function MessageTile() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="callachap-tile-message">
      <div className="flex items-center gap-2 text-slate-900"><MessageCircle className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} /><h3 className="font-heading text-lg">Send a Confidential Message</h3></div>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">Send a private support message through your organization's configured CCCHAPS support channel.</p>
      <Link to="/care-portal/request-support" data-testid="callachap-message-link" className="mt-4 block text-center px-4 py-2.5 rounded-lg border border-slate-300 hover:border-[#4A6FA5] text-sm font-semibold uppercase tracking-wider">
        Open Message Form
      </Link>
    </div>
  );
}

function RemoteSupportTile({ cfg, orgSlug }) {
  const v = cfg.video || { enabled: false };
  const [step, setStep] = useState("idle"); // idle | form | submitting
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [prefTime, setPrefTime] = useState("");
  const [consent, setConsent] = useState(false);

  if (!v.enabled) {
    return (
      <DisabledTile
        icon={Video}
        title="Remote Support"
        body="Additional remote-support options may be available through some organizations' care arrangements. Not currently active for your organization."
      />
    );
  }

  const start = () => setStep("form");
  const submit = async () => {
    if (!consent) { toast.error("Please confirm consent to continue."); return; }
    if (!firstName || !email) { toast.error("First name and email are required."); return; }
    setStep("submitting");
    try {
      const r = await axios.post(`${API}/callachap/${encodeURIComponent(orgSlug)}/video-request`, { first_name: firstName, last_name: lastName, email, phone, preferred_time_note: prefTime, consent: true });
      toast.success("Request captured — opening scheduler…");
      setTimeout(() => { window.open(r.data.redirect_url, "_blank", "noopener"); }, 400);
      setStep("idle");
      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setPrefTime(""); setConsent(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setStep("form");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="callachap-tile-video">
      <div className="flex items-center gap-2 text-slate-900"><Video className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} /><h3 className="font-heading text-lg">Remote Support</h3></div>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">Use additional remote-support options when they are available through your organization's care arrangement.</p>
      {step === "idle" && (
        <button onClick={start} data-testid="callachap-video-start" className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider">
          Request Remote Support <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.2} />
        </button>
      )}
      {(step === "form" || step === "submitting") && (
        <div className="mt-3 space-y-2" data-testid="callachap-video-form">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="callachap-video-first" className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
            <input placeholder="Last name (optional)" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="callachap-video-last" className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
          </div>
          <input placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="callachap-video-email" className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm" />
          <input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="callachap-video-phone" className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm" />
          <input placeholder="Preferred day/time note (optional)" value={prefTime} onChange={(e) => setPrefTime(e.target.value)} data-testid="callachap-video-preftime" className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm" />
          <label className="flex items-start gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="callachap-video-consent" className="mt-0.5 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
            <span>I understand a minimal record is created so CCCHAPS chaplains have operational visibility. No conversation content is stored.</span>
          </label>
          <button onClick={submit} disabled={step === "submitting"} data-testid="callachap-video-submit" className="w-full px-3 py-2 rounded bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-60">{step === "submitting" ? "Submitting…" : "Continue to scheduler"}</button>
        </div>
      )}
    </div>
  );
}

function DisabledTile({ icon: Icon, title, body }) {
  const slug = title.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-5" data-testid={`callachap-tile-${slug}-disabled`}>
      <div className="flex items-center gap-2 text-slate-500"><Icon className="w-5 h-5" strokeWidth={1.75} /><h3 className="font-heading text-lg">{title}</h3></div>
      <p className="mt-1 text-xs text-slate-500 leading-relaxed">
        {body || "Not yet configured."} Please use another channel or <Link to="/care-portal/request-support" className="text-[#4A6FA5] underline">send a confidential message</Link>.
      </p>
    </div>
  );
}
